import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface SquadEntry {
    entryId: number;
    squadId: number;
    squadName: string;
    squadDate: string;
    startTime: string;
    endTime: string;
    teamId: number;
    teamName: string;
    teamLane: number | null;
    leagueId: number;
    leagueName: string;
    leagueVenue: string;
    leagueCity: string;
    teamFormat: string;
    leagueStartDate: string;
    leagueEndDate: string;
    isPositionBased: boolean;
    oilPattern?: string;
}

const BowlerCalendar: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [entries, setEntries] = useState<SquadEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchCalendarData = async () => {
        setLoading(true);
        setError('');
        try {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const response = await fetch(`${apiUrl}/player/calendar`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setEntries(data.entries);
            } else {
                setError(t('player.failedToFetchCalendar'));
            }
        } catch (err) {
            setError(t('player.failedToFetchCalendar'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCalendarData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Group entries by month
    const groupEntriesByMonth = () => {
        const grouped: { [key: string]: { month: string; year: number; entries: SquadEntry[] } } = {};

        entries.forEach(entry => {
            const date = new Date(entry.squadDate);
            const monthYear = `${date.getFullYear()}-${date.getMonth()}`;

            if (!grouped[monthYear]) {
                grouped[monthYear] = {
                    month: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                    year: date.getFullYear(),
                    entries: []
                };
            }
            grouped[monthYear].entries.push(entry);
        });

        return Object.values(grouped).sort((a, b) => {
            const aDate = new Date(a.entries[0].squadDate);
            const bDate = new Date(b.entries[0].squadDate);
            return aDate.getTime() - bDate.getTime();
        });
    };

    // Get entries for a specific date
    const getEntriesForDate = (date: Date, monthEntries: SquadEntry[]) => {
        const dateStr = date.toISOString().split('T')[0];
        return monthEntries.filter(entry =>
            new Date(entry.squadDate).toISOString().split('T')[0] === dateStr
        );
    };

    // Generate calendar dates for a month
    const generateCalendarDates = (entries: SquadEntry[]): { dates: Date[]; firstDay: Date } | null => {
        if (entries.length === 0) return null;

        const firstDate = new Date(entries[0].squadDate);
        const year = firstDate.getFullYear();
        const month = firstDate.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const dates: Date[] = [];
        for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
            dates.push(new Date(d));
        }

        return { dates, firstDay };
    };

    // Get color for team format
    const getColorForFormat = (format: string) => {
        const colors: { [key: string]: string } = {
            'individual': 'bg-yellow-600/40 hover:bg-yellow-600/60 border-yellow-400',
            'duos': 'bg-cyan-500/40 hover:bg-cyan-500/60 border-cyan-400',
            'trios': 'bg-green-600/40 hover:bg-green-600/60 border-green-400',
            'squads': 'bg-purple-600/40 hover:bg-purple-600/60 border-purple-400',
            'fives': 'bg-orange-600/40 hover:bg-orange-600/60 border-orange-400',
        };
        return colors[format] || 'bg-gray-600/40 hover:bg-gray-600/60 border-gray-400';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
                <div className="text-white text-xl">{t('player.loadingCalendar')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
                <div className="text-red-400 text-xl">{error}</div>
            </div>
        );
    }

    const monthGroups = groupEntriesByMonth();

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
            {/* Header */}
            <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/player/dashboard')}
                                className="text-white hover:text-blue-300 transition duration-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <h1 className="text-3xl font-bold text-white">🗓️ {t('player.myBowlingCalendar')}</h1>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {entries.length === 0 ? (
                    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-12 border border-white/20 text-center">
                        <div className="text-6xl mb-4">📅</div>
                        <h2 className="text-2xl font-bold text-white mb-2">{t('player.noSessionsScheduled')}</h2>
                        <p className="text-blue-200 mb-6">{t('player.noUpcomingSessions')}</p>
                        <button
                            onClick={() => navigate('/player/leagues/browse')}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                        >
                            {t('player.browseLeaguesButton')}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Legend */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 mb-6">
                            <h3 className="text-white font-semibold mb-3">{t('player.teamFormatColors')}:</h3>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-yellow-600 rounded border border-yellow-400"></div>
                                    <span className="text-yellow-200">{t('league.formatIndividual')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-cyan-500 rounded border border-cyan-400"></div>
                                    <span className="text-cyan-200">{t('league.formatDuos')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-green-600 rounded border border-green-400"></div>
                                    <span className="text-green-200">{t('league.formatTrios')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-purple-600 rounded border border-purple-400"></div>
                                    <span className="text-purple-200">{t('league.formatSquads')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-orange-600 rounded border border-orange-400"></div>
                                    <span className="text-orange-200">{t('league.formatFives')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Calendar Months */}
                        <div className="space-y-8">
                            {monthGroups.map((group, groupIndex) => {
                                const calendarData = generateCalendarDates(group.entries);
                                if (!calendarData) return null;

                                const { dates, firstDay } = calendarData;

                                return (
                                    <div key={groupIndex} className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                                        <h2 className="text-2xl font-bold text-white mb-6">{group.month}</h2>

                                        <div className="grid grid-cols-7 gap-2">
                                            {/* Day headers */}
                                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                                <div key={day} className="text-center text-sm font-semibold text-blue-200 py-2">
                                                    {day}
                                                </div>
                                            ))}

                                            {/* Empty cells before month starts */}
                                            {Array.from({ length: firstDay.getDay() }, (_, i) => (
                                                <div key={`empty-${i}`} className="min-h-48 bg-white/5 rounded-lg"></div>
                                            ))}

                                            {/* Calendar dates */}
                                            {dates.map((date) => {
                                                const dayEntries = getEntriesForDate(date, group.entries);

                                                return (
                                                    <div
                                                        key={date.toISOString()}
                                                        className="min-h-48 rounded-lg p-2 bg-white/5 flex flex-col"
                                                    >
                                                        <div className="text-sm font-medium text-white mb-1">
                                                            {date.getDate()}
                                                        </div>
                                                        {dayEntries.length > 0 && (
                                                            <div className="flex-1 overflow-y-auto space-y-2 pr-1"
                                                                style={{
                                                                    scrollbarWidth: 'thin',
                                                                    scrollbarColor: 'rgba(255,255,255,0.2) transparent'
                                                                }}
                                                            >
                                                                {dayEntries
                                                                    .filter((entry, idx, arr) =>
                                                                        arr.findIndex(e =>
                                                                            e.entryId === entry.entryId
                                                                        ) === idx
                                                                    )
                                                                    .map((entry) => {
                                                                        const colorClass = getColorForFormat(entry.teamFormat);
                                                                        return (
                                                                            <div
                                                                                key={entry.entryId}
                                                                                onClick={() => navigate(`/player/leagues/${entry.leagueId}`)}
                                                                                className={`${colorClass} border rounded p-2 text-xs text-white cursor-pointer transition duration-200`}
                                                                                title={`${entry.leagueName} - ${entry.leagueVenue}\nTeam: ${entry.teamName}\n${entry.squadName}\n${entry.startTime} - ${entry.endTime}`}
                                                                            >
                                                                                <div className="font-bold mb-1">{entry.squadName}</div>
                                                                                <div className="text-blue-100 mb-1">{entry.leagueName}</div>
                                                                                <div className="text-gray-200 mb-1">
                                                                                    📍 {entry.leagueVenue}{entry.oilPattern ? ` (${entry.oilPattern})` : ''}
                                                                                </div>
                                                                                <div className="text-cyan-200 mb-1">👥 {entry.teamName}</div>
                                                                                <div className="text-yellow-200">🕐 {entry.startTime}</div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default BowlerCalendar;
